import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  RESET_INSURANCE,
  INVALIDATE_INSURANCE_QUOTES,
  INVALIDATE_INSURANCE_PRODUCT_LIST,
  INVALIDATE_PASSPORT_CARD_CUSTOMER_DETAILS,
} from 'actions/actionConstants'
import {
  FETCH_INSURANCE_PRODUCTS,
  FETCH_INSURANCE_QUOTE,
  FETCH_INSURANCE_UPDATE_QUOTE,
  FETCH_PASSPORT_CARD_CUSTOMER_DETAILS,
} from 'actions/apiActionConstants'
import { arrayToObject } from 'lib/array/arrayUtils'
import type { AnyAction } from 'redux'
import { omitKeys } from 'lib/object/objectUtils'

function extractError(error: any) {
  if (typeof error === 'string') {
    return error
  }

  const { errors, message } = error
  if (Array.isArray(errors) && errors.length > 0 && errors[0].message) {
    return errors[0].message
  } else {
    return message
  }
}

export const initialInsuranceState: App.InsuranceState = {
  fetchingQuotes: {},
  fetchQuotesErrors: {},
  productListErrors: {},
  productLists: {},
  fetchingProductLists: {},
  products: {},
  quotes: {},
  updateQuotes: {},
  passportCardCustomerDetails: {},
  fetchingPassportCardCustomerDetails: {},
  passportCardCustomerDetailsErrors: {},
}

const apiCalls = apiReducerSwitch<App.InsuranceState>({
  [FETCH_INSURANCE_QUOTE]: {
    request: (state, action) => ({
      fetchingQuotes: {
        ...state.fetchingQuotes,
        [action.key]: true,
      },
      fetchQuotesErrors: omitKeys([action.key], state.fetchQuotesErrors),
    }),
    success: (state, action) => ({
      fetchingQuotes: {
        ...state.fetchingQuotes,
        [action.key]: false,
      },
      quotes: {
        ...state.quotes,
        [action.key]: action.data,
      },
      fetchQuotesErrors: omitKeys([action.key], state.fetchQuotesErrors),
    }),
    failure: (state, action) => ({
      fetchingQuotes: {
        ...state.fetchingQuotes,
        [action.key]: false,
      },
      fetchQuotesErrors: {
        ...state.fetchQuotesErrors,
        [action.key]: extractError(action.error),
      },
    }),
  },
  [FETCH_INSURANCE_PRODUCTS]: {
    request: (state, action) => ({
      fetchingProductLists: {
        ...state.fetchingProductLists,
        [action.key]: true,
      },
    }),
    success: (state, action: AnyAction & { key: string, data: Array<App.InsuranceProduct> }) => ({
      fetchingProductLists: {
        ...state.fetchingProductLists,
        [action.key]: false,
      },
      productLists: {
        ...state.productLists,
        [action.key]: action.data.map(data => data.id),
      },
      products: {
        ...state.products,
        ...arrayToObject(action.data, data => data.id),
      },
    }),
    failure: (state, action) => ({
      fetchingProductLists: {
        ...state.fetchingProductLists,
        [action.key]: false,
      },
      productListErrors: {
        ...state.productListErrors,
        [action.key]: extractError(action.error),
      },
    }),
  },
  [FETCH_INSURANCE_UPDATE_QUOTE]: {
    request: (state, action) => ({
      updateQuotes: {
        ...state.updateQuotes,
        [action.key]: {
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      updateQuotes: {
        ...state.updateQuotes,
        [action.key]: {
          quote: action.data,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      updateQuotes: {
        ...state.updateQuotes,
        [action.key]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_PASSPORT_CARD_CUSTOMER_DETAILS]: {
    request: (state, action) => ({
      fetchingPassportCardCustomerDetails: {
        ...state.fetchingPassportCardCustomerDetails,
        [action.subscriptionId]: true,
      },
    }),
    success: (state, action) => ({
      fetchingPassportCardCustomerDetails: {
        ...state.fetchingPassportCardCustomerDetails,
        [action.subscriptionId]: false,
      },
      passportCardCustomerDetails: {
        ...state.passportCardCustomerDetails,
        [action.subscriptionId]: action.data,
      },
    }),
    failure: (state, action) => ({
      fetchingPassportCardCustomerDetails: {
        ...state.fetchingPassportCardCustomerDetails,
        [action.subscriptionId]: false,
      },
      passportCardCustomerDetailsErrors: {
        ...state.passportCardCustomerDetailsErrors,
        [action.subscriptionId]: extractError(action.error),
      },
    }),
  },
})

const insuranceReducer = createAppReducer<App.InsuranceState>(initialInsuranceState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [RESET_INSURANCE]: () => initialInsuranceState,
  /**
   * Invalidates all cached quotes while preserving products.
   * This forces a refetch with updated traveller counts, preventing
   * quote/order mismatches that can cause XCover rejections.
   */
  [INVALIDATE_INSURANCE_QUOTES]: (state) => ({
    ...state,
    quotes: {},
    fetchingQuotes: {},
    fetchQuotesErrors: {},
  }),
  [INVALIDATE_INSURANCE_PRODUCT_LIST]: (state, action) => ({
    productLists: omitKeys([action.key], state.productLists),
    productListErrors: omitKeys([action.key], state.productListErrors),
    fetchingProductLists: omitKeys([action.key], state.fetchingProductLists),
  }),
  [INVALIDATE_PASSPORT_CARD_CUSTOMER_DETAILS]: (state, action) => ({
    passportCardCustomerDetails: omitKeys([action.subscriptionId], state.passportCardCustomerDetails),
    passportCardCustomerDetailsErrors: omitKeys([action.subscriptionId], state.passportCardCustomerDetailsErrors),
    fetchingPassportCardCustomerDetails: omitKeys([action.subscriptionId], state.fetchingPassportCardCustomerDetails),
  }),
})

export default insuranceReducer
